/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SearchInput} from 'UI/components/action/SearchInput';
import {SeoHead} from 'UI/components/layout/SeoHead';
import {Headline, P} from 'UI/components/layout/Text';
import {TourListItem} from 'UI/components/modules/TourListItem';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {formatDuration, formatPrice, getCategory, getCoverUrl, useHomePage} from 'UI/hooks/useHomePage';
import {AppLayout} from 'UI/layouts/AppLayout';
import {BlockRenderer} from 'UI/modules/BlockRenderer';

import {CONTENT_POPULATE} from 'Payload/utils/contentPopulate';
import {getLegalPages} from 'Payload/utils/getLegalPages';
import {CATEGORY_ICON_MAP} from 'UI/utils/categoryIconMap';

import type {GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Category, Config, Homepage as HomepageType, LegalPage, Walk} from 'types/payload-types';

const DEFAULT_GAP = 4;
const REVALIDATE_SECONDS = 18000;

interface HomeProps {
    categories: Category[];
    homepage: HomepageType | null;
    legalPages: LegalPage[];
    walks: Walk[];
}

/**
 * Startseite mit konfigurierbaren Blöcken aus dem Payload Homepage-Global.
 *
 * @param props            Die Seiten-Props.
 * @param props.categories Array aller Kategorien.
 * @param props.homepage   Das Homepage-Global-Dokument mit Block-Konfiguration.
 * @param props.walks      Array aller veröffentlichten Walks.
 * @returns ReactNode der Startseite.
 */
const Home: NextSSGPageWithLayout<typeof getStaticProps> = (
    {categories, homepage, walks}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {
        contentBlocks,
        filtered,
        handleSearch,
        heroBlocks,
        isSearching,
        navigateToWalk,
        search,
        t
    } = useHomePage(walks, categories, homepage);

    return (
        <Content>
            <SeoHead
                description={homepage?.meta?.description}
                title={homepage?.meta?.title ?? t('home.title')}
                url={process.env.NEXT_PUBLIC_BASE_URL}
            />
            {heroBlocks && (
                <BlockRenderer blocks={heroBlocks} testId="HeroBlocks" />
            )}
            <Section>
                <SearchInput
                    placeholder={t('home.searchPlaceholder')}
                    value={search}
                    onChange={handleSearch}
                />
            </Section>
            {isSearching && filtered.length > 0 && (
                <SearchResults>
                    {filtered.map(walk => {
                        const firstCat = walk.categories?.find(c => typeof c === 'object');
                        const IconComp = firstCat && typeof firstCat === 'object' && firstCat.predefinedIcon
                            ? CATEGORY_ICON_MAP[firstCat.predefinedIcon]
                            : null;

                        return (
                            <TourListItem
                                key={walk.id}
                                category={getCategory(walk, t)}
                                categoryIcon={IconComp ? <IconComp /> : undefined}
                                duration={formatDuration(walk)}
                                price={formatPrice(walk)}
                                thumbnailUrl={getCoverUrl(walk)}
                                title={walk.title}
                                onClick={() => navigateToWalk(walk.slug)}
                            />
                        );
                    })}
                </SearchResults>
            )}
            {!isSearching && (
                <BlockRenderer blocks={contentBlocks} />
            )}
            {filtered.length === 0 && (
                <EmptyState>
                    <Headline $align="center" $size="title" as="p">{t('home.noResults')}</Headline>
                    <P $align="center" $size="body">{t('home.noResultsHint')}</P>
                </EmptyState>
            )}
        </Content>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout (Header + Navigation).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
Home.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="home" legalPages={pageProps.legalPages}>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
Home.getLayoutKey = () => 'home';

export default Home;

// ── Styled ──────────────────────────────────────────────────

interface SectionProps {
    /** Vertical gap between section children in spacing units. */
    $gap?: number;
}

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(12)};
    padding: ${spacing(4)} ${spacing(6)};
`;

const Section = styled.section<SectionProps>`
    display: flex;
    flex-direction: column;
    gap: ${({$gap}) => spacing($gap ?? DEFAULT_GAP)};
`;

const SearchResults = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    padding: ${spacing(12)} 0;
`;

/**
 * Lädt Homepage-Global, Walks, Kategorien und Städte.
 *
 * @param props                  Die Static-Props Kontext-Optionen.
 * @param props.locale           Die aktuelle Locale.
 * @param props.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit Homepage-Config, Walks, Categories und Cities.
 */
export const getStaticProps: GetStaticProps<HomeProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {
                categories: [],
                homepage: null,
                legalPages: [],
                walks: []
            },
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [homepageResult, walksResult, categoriesResult, waypointCountResult, legalPages] = await Promise.all([
        payload.findGlobal({
            depth: 1,
            locale: payloadLocale,
            populate: CONTENT_POPULATE,
            slug: 'homepage'
        }),
        payload.find({
            collection: 'walks',
            depth: 1,
            joins: {waypoints: false},
            limit: 0,
            locale: payloadLocale,
            select: {
                categories: true,
                city: true,
                coverImage: true,
                general: true,
                pricing: true,
                shortDescription: true,
                slug: true,
                title: true
            },
            where: {_status: {equals: 'published'}}
        }),

        payload.find({
            collection: 'categories',
            depth: 1,
            limit: 0,
            locale: payloadLocale
        }),

        payload.find({
            collection: 'waypoints',
            depth: 0,
            limit: 0,
            select: {walk: true}
        }),

        getLegalPages(payload, payloadLocale)
    ]);

    const waypointCountsByWalk = new Map<string, number>();

    for (const wp of waypointCountResult.docs) {
        const walkId = typeof wp.walk === 'string' ? wp.walk : wp.walk.id;

        if (walkId) {
            waypointCountsByWalk.set(walkId, (waypointCountsByWalk.get(walkId) ?? 0) + 1);
        }
    }

    const leanWalks = walksResult.docs.map(walk => ({
        ...walk,
        waypoints: {totalDocs: waypointCountsByWalk.get(walk.id) ?? 0}
    }));

    const homepagetems = {
        ...homepageResult,
        blocks: homepageResult.blocks?.map(b => {
            if (b.blockType !== 'featured-walks-block') {
                return b;
            }

            return {
                ...b,
                walks: b.walks?.map(walk => {
                    const id = typeof walk === 'string' ? walk : walk.id;

                    return leanWalks.find(w => w.id === id);
                }).filter((walk): walk is typeof leanWalks[number] => walk !== undefined)
            };
        })
    };

    return {
        props: {
            categories: JSON.parse(JSON.stringify(categoriesResult.docs)) as Category[],
            homepage: JSON.parse(JSON.stringify(homepagetems)) as HomepageType,
            legalPages,
            walks: JSON.parse(JSON.stringify(leanWalks)) as Walk[]
        },
        revalidate: REVALIDATE_SECONDS
    };
};